import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBox, faSpinner, faShoppingBag, faFilter
} from '@fortawesome/free-solid-svg-icons';
import { ordersAPI } from '../services/api';
import '../assets/css/ordersList.css';

const OrdersList = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', name: 'All Orders' },
    { id: 'pending', name: 'Pending' },
    { id: 'confirmed', name: 'Confirmed' },
    { id: 'shipped', name: 'Shipped' },
    { id: 'delivered', name: 'Delivered' },
    { id: 'cancelled', name: 'Cancelled' }
  ];

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getUserOrders();
      const ordersList = response.data || [];
      setOrders(ordersList);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadOrders();
    setRefreshing(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#F59E0B';
      case 'confirmed': return '#3B82F6';
      case 'processing': return '#8B5CF6';
      case 'shipped': return '#10B981';
      case 'delivered': return '#22C55E';
      case 'cancelled': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const filteredOrders = orders.filter(order => 
    activeFilter === 'all' || order.status === activeFilter
  );

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(price || 0);
  };

  if (loading) {
    return (
      <div className="orders-list-page">
        <div className="orders-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-list-page">
      <div className="orders-container">
        <div className="orders-header">
          <h1>My Orders</h1>
          <button onClick={handleRefresh} disabled={refreshing} className="btn-refresh">
            <FontAwesomeIcon icon={faSpinner} spin={refreshing} /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="orders-filters">
          {filters.map((filter) => (
            <button
              key={filter.id}
              className={`filter-chip ${activeFilter === filter.id ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.name}
            </button>
          ))}
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="orders-empty">
            <FontAwesomeIcon icon={faBox} size="4x" />
            <h2>No orders found</h2>
            <p>
              {activeFilter === 'all' 
                ? "You haven't placed any orders yet"
                : `No ${activeFilter} orders found`}
            </p>
            <button onClick={() => navigate('/shop')} className="btn-primary">
              <FontAwesomeIcon icon={faShoppingBag} /> Start Shopping
            </button>
          </div>
        ) : (
          <div className="orders-grid">
            {filteredOrders.map((order) => (
              <div 
                key={order.id || order._id} 
                className="order-card"
                onClick={() => navigate(`/orders/${order.id || order._id}`)}
              >
                <div className="order-card-header">
                  <div>
                    <h3>Order #{order?.order_number || order?.orderNumber || 'N/A'}</h3>
                    <p className="order-date">
                      {order?.created_at || order?.createdAt 
                        ? new Date(order?.created_at || order?.createdAt).toLocaleDateString() 
                        : 'N/A'}
                    </p>
                  </div>
                  <span 
                    className="status-badge" 
                    style={{ backgroundColor: `${getStatusColor(order.status)}20`, color: getStatusColor(order.status) }}
                  >
                    {order.status ? order.status.toUpperCase() : 'UNKNOWN'}
                  </span>
                </div>

                <div className="order-items-preview">
                  {(order.items || []).slice(0, 2).map((item, index) => (
                    <div key={index} className="order-item-preview">
                      <img 
                        src={item?.product_image || item?.product?.images?.[0]?.url || 'https://via.placeholder.com/50'} 
                        alt={item?.product_title || item?.product?.title || 'Product'}
                      />
                      <div className="item-preview-details">
                        <p className="item-title">
                          {item?.product_title || item?.product?.title || 'Unknown Product'}
                        </p>
                        <p className="item-price">
                          {formatPrice(item?.unit_price || item?.price || 0)} × {item?.quantity || 0}
                        </p>
                      </div>
                    </div>
                  ))}
                  {(order.items || []).length > 2 && (
                    <p className="more-items">+{(order.items || []).length - 2} more items</p>
                  )}
                </div>

                <div className="order-card-footer">
                  <span className="order-total">
                    Total: {formatPrice(Number(order?.total_amount) || 0)}
                  </span>
                  <span className="view-details">View Details →</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersList;
